import { Dayjs } from 'dayjs';

import { FileAttachment } from '~hooks/useFileAttachment/models';
import { Timesheet } from '~hooks/useTimesheets/models';

// A normalized row shape shared by the driver-day and job timesheet cards so they can render one
// list regardless of whether a row came from the legacy FileAttachment path (flag OFF, or a
// pre-existing sheet during the dual-read transition) or the new Timesheet model (flag ON). The
// `source` discriminator tells the delete handler which endpoint to call, since the two paths use
// different id-spaces (`deleteId` is a FileAttachment id or a Timesheet id accordingly).
export interface TimesheetListItem {
  key: string;
  fileName: string;
  fileUrl: string | null;
  previewUrl: string | null;
  deleteId: string;
  source: 'fileAttachment' | 'timesheet';
  deletable: boolean;
  aiProcessed: boolean;
  startedAt: Dayjs | null;
  endedAt: Dayjs | null;
  totalMinutes: number | null;
  extractedStartAt: Dayjs | null;
  extractedEndAt: Dayjs | null;
  extractedTotalMinutes: number | null;
  // The canonical slip number (user-entered or AI-seeded) and the raw AI-parsed number.
  // Legacy FileAttachment rows have neither.
  timesheetNumber: string | null;
  extractedNumber: string | null;
}

export const fileAttachmentToListItem = (
  fileAttachment: FileAttachment,
): TimesheetListItem => ({
  key: `fileAttachment:${fileAttachment.id}`,
  fileName: fileAttachment.fileName,
  fileUrl: fileAttachment.fileUrl,
  previewUrl: fileAttachment.previewUrl ?? null,
  deleteId: fileAttachment.id,
  source: 'fileAttachment',
  deletable: fileAttachment.deletable,
  aiProcessed: false,
  startedAt: null,
  endedAt: null,
  totalMinutes: null,
  extractedStartAt: null,
  extractedEndAt: null,
  extractedTotalMinutes: null,
  timesheetNumber: null,
  extractedNumber: null,
});

export const timesheetToListItem = (timesheet: Timesheet): TimesheetListItem => ({
  key: `timesheet:${timesheet.id}`,
  fileName: timesheet.fileAttachment?.fileName ?? timesheet.timesheetNumber ?? '',
  timesheetNumber: timesheet.timesheetNumber ?? null,
  extractedNumber: timesheet.extractedNumber ?? null,
  fileUrl: timesheet.fileAttachment?.fileUrl ?? null,
  previewUrl: timesheet.fileAttachment?.previewUrl ?? null,
  deleteId: timesheet.id,
  // The new model has no per-record `deletable`; treat all as deletable (matches TimesheetDrawer).
  source: 'timesheet',
  deletable: true,
  aiProcessed: timesheet.aiProcessed,
  startedAt: timesheet.startedAt ?? null,
  endedAt: timesheet.endedAt ?? null,
  totalMinutes: timesheet.totalMinutes ?? null,
  extractedStartAt: timesheet.extractedStartAt ?? null,
  extractedEndAt: timesheet.extractedEndAt ?? null,
  extractedTotalMinutes: timesheet.extractedTotalMinutes ?? null,
});
